/** @jsxImportSource @emotion/react */
import { useState } from 'react'
import styled from '@emotion/styled'
import { Alert, Button, Modal } from 'react-bootstrap'

import {
  useGetExternalApiCredentialQuery,
  useCreateExternalApiCredentialMutation,
  useUpdateExternalApiCredentialMutation,
  useDeactivateExternalApiCredentialMutation,
  CreateExternalApiCredentialDto,
  UpdateExternalApiCredentialDto,
  CreateExternalApiCredentialResponse,
} from 'store/services/externalApi'
import Input from 'components/atoms/Input'
import Checkbox from 'components/atoms/Checkbox'

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const StatusCard = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
`

const StatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`

const Label = styled.span`
  color: #6d757e;
  font-weight: 500;
`

const Value = styled.span`
  color: #212529;
`

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`

const FormGroup = styled.div`
  margin-bottom: 16px;
`

const FormLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  color: #6d757e;
  font-weight: 500;
`

const CredentialInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  .form-control {
    width: auto;
    min-width: 300px;
    max-width: 600px;
  }
`

const InfoAlert = styled(Alert)`
  background-color: #e7f3ff;
  border-color: #b3d9ff;
  color: #004085;
`

interface ExternalApiPanelProps {
  companyId: number
  companyName?: string
}

export const ExternalApiPanel = ({ companyId, companyName }: ExternalApiPanelProps) => {
  const { data: credential, isLoading, refetch } = useGetExternalApiCredentialQuery(companyId)
  const [createCredential, { isLoading: isCreating }] = useCreateExternalApiCredentialMutation()
  const [updateCredential, { isLoading: isUpdating }] = useUpdateExternalApiCredentialMutation()
  const [deactivateCredential, { isLoading: isDeactivating }] =
    useDeactivateExternalApiCredentialMutation()

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showCredentialsModal, setShowCredentialsModal] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isRegenerating, setIsRegenerating] = useState(false)

  // Create form state
  const [createForm, setCreateForm] = useState<CreateExternalApiCredentialDto>({
    displayName: companyName || '',
  })

  // Edit form state
  const [editForm, setEditForm] = useState<UpdateExternalApiCredentialDto>({
    slug: credential?.slug || '',
    displayName: credential?.displayName || '',
    isActive: credential?.isActive ?? true,
  })

  // Generated credentials (one-time display)
  const [generatedCredentials, setGeneratedCredentials] = useState<{
    clientId: string
    clientSecret: string
  } | null>(null)

  const handleCreate = async () => {
    try {
      setError(null)
      const result: CreateExternalApiCredentialResponse = await createCredential({
        companyId,
        data: createForm,
      }).unwrap()

      // Store generated credentials for one-time display
      setGeneratedCredentials({
        clientId: result.clientId,
        clientSecret: result.clientSecret,
      })

      setShowCreateModal(false)
      setShowCredentialsModal(true)
      setCreateForm({
        displayName: companyName || '',
      })
      refetch()
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to create external API credential')
    }
  }

  const handleUpdate = async () => {
    try {
      setError(null)
      await updateCredential({
        companyId,
        data: editForm,
      }).unwrap()
      setShowEditModal(false)
      refetch()
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to update external API credential')
    }
  }

  const handleDeactivate = async () => {
    if (
      !window.confirm(
        'Are you sure you want to delete external API access for this company? This action cannot be undone.',
      )
    ) {
      return
    }

    try {
      setError(null)
      await deactivateCredential(companyId).unwrap()
      refetch()
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to delete external API credential')
    }
  }

  const handleRegenerateKeys = async () => {
    if (
      !window.confirm(
        'Are you sure you want to regenerate API keys? The old keys will be invalidated and new keys will be generated. Make sure to update any systems using the old keys.',
      )
    ) {
      return
    }

    try {
      setError(null)
      setIsRegenerating(true)

      // Deactivate current credential
      await deactivateCredential(companyId).unwrap()

      // Create new credential with same display name
      const result: CreateExternalApiCredentialResponse = await createCredential({
        companyId,
        data: {
          displayName: credential?.displayName || companyName || 'External API Client',
        },
      }).unwrap()

      // Store generated credentials for one-time display
      setGeneratedCredentials({
        clientId: result.clientId,
        clientSecret: result.clientSecret,
      })

      setShowCredentialsModal(true)
      refetch()
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to regenerate API keys')
    } finally {
      setIsRegenerating(false)
    }
  }

  const handleOpenEdit = () => {
    setEditForm({
      slug: credential?.slug || '',
      displayName: credential?.displayName || '',
      isActive: credential?.isActive ?? true,
    })
    setShowEditModal(true)
  }

  if (isLoading) {
    return <Container>Loading...</Container>
  }

  return (
    <Container>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {credential ? (
        <>
          <StatusCard>
            <StatusRow>
              <Label>Status:</Label>
              <Value>
                <span
                  style={{
                    color: credential.isActive ? '#28a745' : '#dc3545',
                    fontWeight: 'bold',
                  }}>
                  {credential.isActive ? 'Active' : 'Inactive'}
                </span>
              </Value>
            </StatusRow>
            <StatusRow>
              <Label>Display Name:</Label>
              <Value>{credential.displayName || 'N/A'}</Value>
            </StatusRow>
            <StatusRow>
              <Label>Slug:</Label>
              <Value>{credential.slug}</Value>
            </StatusRow>
            <StatusRow>
              <Label>API Base URL:</Label>
              <Value>{`/external/api/${credential.slug}`}</Value>
            </StatusRow>
            <StatusRow>
              <Label>Created:</Label>
              <Value>
                {credential.createdAt
                  ? new Date(credential.createdAt).toLocaleDateString()
                  : 'N/A'}
              </Value>
            </StatusRow>
          </StatusCard>

          <ActionButtons>
            <Button variant="primary" onClick={handleOpenEdit} disabled={isUpdating}>
              Edit Configuration
            </Button>
            <Button
              variant="warning"
              onClick={handleRegenerateKeys}
              disabled={isRegenerating || isDeactivating}>
              {isRegenerating ? 'Regenerating...' : 'Regenerate API Keys'}
            </Button>
            {credential.isActive && (
              <Button
                variant="danger"
                onClick={handleDeactivate}
                disabled={isDeactivating || isRegenerating}>
                {isDeactivating ? 'Deactivating...' : 'Delete'}
              </Button>
            )}
          </ActionButtons>
        </>
      ) : (
        <>
          <StatusCard>
            <p style={{ color: '#6d757e', margin: 0 }}>
              External API access is not enabled for this company. Enable it to allow external
              clients to access this company's data via the API.
            </p>
          </StatusCard>

          <ActionButtons>
            <Button variant="primary" onClick={() => setShowCreateModal(true)}>
              Enable External API Access
            </Button>
          </ActionButtons>
        </>
      )}

      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header>
          <Modal.Title>Enable External API Access</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormGroup>
            <FormLabel>Display Name *</FormLabel>
            <Input
              parentDiv={null}
              name="displayName"
              className="form-control"
              placeholder="Display Name"
              value={createForm.displayName}
              handleChange={(e) =>
                setCreateForm({ ...createForm, displayName: e.target.value })
              }
            />
            <small className="text-muted">
              A URL-safe identifier (slug) will be automatically generated from this name.
            </small>
          </FormGroup>

          <InfoAlert className="mt-3">
            <strong>Note:</strong> API keys (Client ID and Client Secret) and a URL slug will be
            automatically generated and displayed once after creation. Make sure to copy and store
            the API keys securely as they will not be shown again.
          </InfoAlert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={isCreating || !createForm.displayName}>
            {isCreating ? 'Generating API Keys...' : 'Generate API Keys'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header>
          <Modal.Title>Edit External API Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormGroup>
            <FormLabel>Display Name</FormLabel>
            <Input
              parentDiv={null}
              name="displayName"
              className="form-control"
              placeholder="Display Name"
              value={editForm.displayName}
              handleChange={(e) =>
                setEditForm({ ...editForm, displayName: e.target.value })
              }
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Slug</FormLabel>
            <Input
              parentDiv={null}
              name="slug"
              className="form-control"
              placeholder="URL-safe identifier"
              value={editForm.slug}
              handleChange={(e) => setEditForm({ ...editForm, slug: e.target.value })}
            />
            <small className="text-muted">
              Must be lowercase letters, numbers, and hyphens only.
            </small>
          </FormGroup>

          <FormGroup>
            <Checkbox
              handleChange={(e) =>
                setEditForm({ ...editForm, isActive: e.target.checked })
              }
              value={editForm.isActive ?? true}
              label="Active"
              parentClassName="custom-control"
            />
          </FormGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdate}
            disabled={isUpdating || !editForm.displayName || !editForm.slug}>
            {isUpdating ? 'Updating...' : 'Update'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Credentials Created Modal */}
      <Modal show={showCredentialsModal} onHide={() => setShowCredentialsModal(false)} size="lg">
        <Modal.Header>
          <Modal.Title>API Keys Generated</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <strong>Important:</strong> These credentials will only be shown once. Please copy and
            store them securely. They cannot be retrieved later.
          </Alert>

          <FormGroup>
            <FormLabel>Client ID (API Key)</FormLabel>
            <CredentialInputWrapper>
              <Input
                parentDiv={null}
                name="clientId"
                className="form-control font-monospace"
                value={generatedCredentials?.clientId || ''}
                readOnly
              />
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  if (generatedCredentials?.clientId) {
                    navigator.clipboard.writeText(generatedCredentials.clientId)
                  }
                }}>
                <i className="fas fa-copy"></i> Copy
              </Button>
            </CredentialInputWrapper>
          </FormGroup>

          <FormGroup>
            <FormLabel>Client Secret</FormLabel>
            <CredentialInputWrapper>
              <Input
                parentDiv={null}
                name="clientSecret"
                type="password"
                className="form-control font-monospace"
                value={generatedCredentials?.clientSecret || ''}
                readOnly
              />
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  if (generatedCredentials?.clientSecret) {
                    navigator.clipboard.writeText(generatedCredentials.clientSecret)
                  }
                }}>
                <i className="fas fa-copy"></i> Copy
              </Button>
            </CredentialInputWrapper>
          </FormGroup>

          <InfoAlert className="mt-3">
            <p className="mb-0">
              <strong>API Base URL:</strong>{' '}
              <code>{`/external/api/${credential?.slug || 'your-slug'}`}</code>
            </p>
            <p className="mb-0 mt-2">
              Use these credentials for HTTP Basic Authentication when calling the external API.
            </p>
          </InfoAlert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              setShowCredentialsModal(false)
              setGeneratedCredentials(null) // Clear credentials after closing
            }}>
            I've Saved These Credentials
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
